// docs/plan/loader-history-traceability-2026-07 — 로더 편집 모달(공유).
// 로더 상세 + 로더 이력 목록의 행 편집 아이콘에서 공용으로 사용. loaderId 로 최신
// 상세를 조회해 폼을 채우므로, 목록 요약에 없는 필드도 안전하게 편집한다.
// 편집 대상: serial / vendor / receivedAt / inspectionResult / status / notes + 적용 개조.
//
// 개선 9건 차수1(26-08-04) — 시리얼 칸을 열었다. 오너 결정 '콘솔이 정본'에 따라
// 잘못 등록된 SN 을 콘솔에서 고쳐야 하는데, 종전엔 편집 대상이 아니었다.
//
// 개조 체크박스(LoaderRevPicker)는 등록 폼(LoadersListPage)과 같은 UI 라 여기서
// export 한다 — LoadersListPage 가 이미 이 모듈을 import 하므로 순환이 없다.

import { useMemo, useState, type CSSProperties } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatLoaderSerial,
  getHolidayName,
  isKoreanHoliday,
  LOADER_INSPECTION_LABELS,
  LOADER_STATUS_LABELS,
  LoaderInspection,
  LoaderStatus,
  type LoaderRevDto,
} from '@bhops/shared';
import { loaderRevsApi, loadersApi, type LoaderDetail } from '@/api/loaders';
import { extractApiError } from '@/api/client';
import { Tooltip } from '@/components/Tooltip';
import { Button, Field, Input, Textarea, FormError } from '@/components/Form';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Modal } from '@/components/Modal';
import { toYmdInput } from '@/lib/format-date';

// ============================================================
// 적용 개조 체크박스 (등록 폼과 공용)
// ============================================================

export interface LoaderRevOption {
  id: string;
  code: string;
  displayName: string;
  description?: string | null;
  /** 연결된 로봇 변경점(ECN) 코드. */
  ecnCodes?: string[];
  appliedLoaderCount?: number;
  autoApplyToNewLoaders?: boolean;
}

export function toLoaderRevOption(rev: LoaderRevDto): LoaderRevOption {
  return {
    id: rev.id,
    code: rev.code,
    displayName: rev.displayName,
    description: rev.description,
    ecnCodes: rev.ecnLinks.map((l) => l.code).filter(Boolean),
    appliedLoaderCount: rev.appliedLoaderCount,
    autoApplyToNewLoaders: rev.autoApplyToNewLoaders,
  };
}

// 마우스를 올렸을 때 보여줄 설명. 공용 Tooltip 컴포넌트가 생기면 이 문자열을
// 그대로 넘겨 교체한다.
export function loaderRevTooltip(rev: LoaderRevOption): string {
  const lines = [`${rev.code} · ${rev.displayName}`];
  if (rev.description) lines.push(rev.description);
  lines.push(
    rev.ecnCodes?.length ? `연결된 변경점: ${rev.ecnCodes.join(', ')}` : '연결된 변경점 없음',
  );
  lines.push(`적용 로더 ${rev.appliedLoaderCount ?? 0}대`);
  return lines.join('\n');
}

export function LoaderRevPicker({
  options,
  selected,
  onToggle,
  loading,
  error,
  idPrefix,
  showAutoBadge,
}: {
  options: LoaderRevOption[];
  selected: readonly string[];
  onToggle: (revId: string, checked: boolean) => void;
  loading?: boolean;
  error?: unknown;
  idPrefix: string;
  showAutoBadge?: boolean;
}) {
  const selectedSet = new Set(selected);
  if (loading) return <div style={pickerNote}>불러오는 중…</div>;
  if (error) return <FormError>{extractApiError(error)}</FormError>;
  if (options.length === 0) return <div style={pickerNote}>등록된 개조가 없습니다.</div>;
  return (
    <div style={pickerList}>
      {options.map((rev) => (
        // focusable={false} — 안의 체크박스가 이미 탭 정지점이라 wrapper 까지
        // 정지점이 되면 개조 하나당 탭이 두 번 걸린다(포커스는 버블된다).
        <Tooltip key={rev.id} content={loaderRevTooltip(rev)} focusable={false}>
          <label style={pickerRow}>
            <input
              type="checkbox"
              data-testid={`${idPrefix}-rev-${rev.id}`}
              checked={selectedSet.has(rev.id)}
              onChange={(e) => onToggle(rev.id, e.target.checked)}
            />
            <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{rev.code}</span>
            <span style={pickerName}>{rev.displayName}</span>
            {showAutoBadge && rev.autoApplyToNewLoaders && <span style={autoBadge}>자동 적용</span>}
          </label>
        </Tooltip>
      ))}
    </div>
  );
}

export function EditLoaderModal({ loaderId, onClose }: { loaderId: string; onClose: () => void }) {
  const q = useQuery({
    queryKey: ['loader', loaderId],
    queryFn: () => loadersApi.get(loaderId),
    staleTime: 10_000,
  });
  return (
    <Modal open onClose={onClose} title="로더 편집" width={520}>
      {q.isLoading ? (
        <div style={{ padding: 24, color: 'var(--fg-muted)', fontSize: 13 }}>불러오는 중…</div>
      ) : q.error ? (
        <FormError>{extractApiError(q.error)}</FormError>
      ) : q.data ? (
        <EditLoaderForm loader={q.data} onClose={onClose} />
      ) : null}
    </Modal>
  );
}

function EditLoaderForm({ loader, onClose }: { loader: LoaderDetail; onClose: () => void }) {
  const qc = useQueryClient();
  // 개선 9건 차수1(26-08-04) — 시리얼 수정. 형식은 서버도 강제하지 않는다(등록과 동일).
  const [serial, setSerial] = useState(loader.serial);
  const [serialConfirmOpen, setSerialConfirmOpen] = useState(false);
  const [vendor, setVendor] = useState(loader.vendor ?? '');
  // `<input type="date">` 프리필 — 저장은 아래 `${receivedAt}T00:00:00.000Z`(UTC 자정)
  // 이라 KST 로 읽으면 같은 날짜로 되돌아온다(자정 + 9h = 그날 09:00).
  const [receivedAt, setReceivedAt] = useState(toYmdInput(loader.receivedAt));
  const [inspectionResult, setInspectionResult] = useState<LoaderInspection>(
    loader.inspectionResult,
  );
  const [status, setStatus] = useState<LoaderStatus>(loader.status);
  const [notes, setNotes] = useState(loader.notes ?? '');
  // 편집에서는 자동 적용 프리체크를 하지 않는다 — 이미 적용된 것이 진실.
  const [revIds, setRevIds] = useState<string[]>(() => loader.revs.map((r) => r.loaderRevId));

  const revsQ = useQuery({
    queryKey: ['loader-revs', 'picker'],
    queryFn: () => loaderRevsApi.list(),
    staleTime: 60_000,
  });

  const revOptions = useMemo<LoaderRevOption[]>(() => {
    const active = (revsQ.data?.items ?? []).map(toLoaderRevOption);
    const known = new Set(active.map((o) => o.id));
    // 보관된 개조라도 이미 적용돼 있으면 보여준다 — 안 그러면 체크 해제로 뗄 수 없다.
    const archived = loader.revs
      .filter((r) => !known.has(r.loaderRevId))
      .map((r) => ({ id: r.loaderRevId, code: r.code, displayName: r.displayName }));
    return [...active, ...archived];
  }, [revsQ.data, loader.revs]);

  const trimmedSerial = serial.trim();
  const serialChanged = trimmedSerial !== loader.serial;

  const updateM = useMutation({
    mutationFn: () =>
      // 빈 값은 undefined 가 아니라 빈 문자열/ null 로 보내 서버가 실제로 지우게 한다
      // (서버: 빈 문자열 → null 로 저장). 안 그러면 값을 비워도 이전 값이 남는다.
      loadersApi.update(loader.id, {
        // 개선 9건 차수1 — 바뀌었을 때만 보낸다. 서버는 값이 같으면 빠른 경로를
        // 타지만, 안 보내는 편이 "무엇을 바꾸려는지"를 요청이 스스로 말한다
        // (짐작 대신 선언 — 서버가 payload 를 보고 추측하지 않게 한다).
        ...(serialChanged ? { serial: trimmedSerial } : {}),
        vendor: vendor.trim(),
        receivedAt: receivedAt ? `${receivedAt}T00:00:00.000Z` : null,
        inspectionResult,
        status,
        notes: notes.trim(),
        revIds,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loader', loader.id] });
      qc.invalidateQueries({ queryKey: ['loaders'] });
      // 로봇 상세의 '로더' 탭에 복사된 시리얼도 서버가 함께 갱신했으므로 무효화.
      qc.invalidateQueries({ queryKey: ['robot-loader'] });
      onClose();
    },
  });

  function onSave(): void {
    // 시리얼을 바꿀 때만 영향 분석 확인창(RobotEditModal 의 SN 변경 패턴).
    if (serialChanged) {
      setSerialConfirmOpen(true);
      return;
    }
    updateM.mutate();
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* 개선 9건 차수1(26-08-04) — 시리얼 수정 칸.
          형식을 강제하지 않는다: 원문 serial 이 유일한 진실원이고 디코드는 표시용
          best-effort 라, 수정에만 형식을 걸면 정작 고치려는 옛/잘못 형식 시리얼을
          못 고친다. 서버는 유일성만 강제한다(중복 → 409). */}
      <Field
        label="시리얼"
        htmlFor="loader-serial-edit"
        hint={
          serialChanged
            ? `저장하면 확인 창이 뜹니다. 이력과 개조 기록은 그대로 옮겨집니다. 새 시리얼: ${formatLoaderSerial(trimmedSerial)}`
            : '잘못 등록된 시리얼을 고칠 수 있습니다. 이미 있는 시리얼로는 바꿀 수 없습니다.'
        }
      >
        <Input
          id="loader-serial-edit"
          value={serial}
          onChange={(e) => setSerial(e.target.value)}
          style={{ fontFamily: 'var(--font-mono)' }}
        />
      </Field>
      <Field label="업체" htmlFor="loader-vendor">
        <Input
          id="loader-vendor"
          value={vendor}
          onChange={(e) => setVendor(e.target.value)}
          placeholder="예: H5"
        />
      </Field>
      {/* 추가수정 2차(26-07-27) 정정 — 공휴일 경고(결정 ③A) 확산 누락분.
          등록 창(LoadersListPage)과 같은 칸이므로 같은 문구를 쓴다. */}
      <Field
        label="입고일"
        htmlFor="loader-received"
        hint={
          isKoreanHoliday(receivedAt) ? `공휴일 (${getHolidayName(receivedAt)}) 입니다` : undefined
        }
      >
        <Input
          id="loader-received"
          type="date"
          value={receivedAt}
          onChange={(e) => setReceivedAt(e.target.value)}
        />
      </Field>
      <Field label="입고검사" htmlFor="loader-inspection">
        <select
          id="loader-inspection"
          value={inspectionResult}
          onChange={(e) => setInspectionResult(e.target.value as LoaderInspection)}
          style={selectStyle}
        >
          {Object.entries(LOADER_INSPECTION_LABELS).map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </select>
      </Field>
      <Field label="상태" htmlFor="loader-status">
        <select
          id="loader-status"
          value={status}
          onChange={(e) => setStatus(e.target.value as LoaderStatus)}
          style={selectStyle}
        >
          {Object.entries(LOADER_STATUS_LABELS).map(([v, label]) => (
            <option key={v} value={v}>
              {label}
            </option>
          ))}
        </select>
      </Field>
      <Field label="적용 개조" hint="체크한 개조로 전체 교체됩니다. 구성코드는 자동 재계산됩니다.">
        <LoaderRevPicker
          idPrefix="edit-loader"
          options={revOptions}
          selected={revIds}
          loading={revsQ.isLoading}
          error={revsQ.error}
          onToggle={(revId, checked) =>
            setRevIds((prev) => (checked ? [...prev, revId] : prev.filter((cur) => cur !== revId)))
          }
        />
      </Field>
      <Field label="메모" htmlFor="loader-notes">
        <Textarea id="loader-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
      </Field>

      {/* 개선 9건 차수1 — 시리얼 확인창이 열려 있으면 그 안에서 사유를 보여주므로
          같은 문장을 폼에도 겹쳐 띄우지 않는다. */}
      {updateM.isError && !serialConfirmOpen && (
        <FormError>{extractApiError(updateM.error)}</FormError>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
        <Button type="button" variant="secondary" onClick={onClose}>
          취소
        </Button>
        <Button
          type="button"
          onClick={onSave}
          disabled={updateM.isPending || trimmedSerial.length === 0}
        >
          {updateM.isPending ? '저장 중…' : '저장'}
        </Button>
      </div>

      {/* 개선 9건 차수1(26-08-04) — 시리얼 변경 영향 분석.
          RobotEditModal 의 SN 변경 확인창과 같은 방식: 무엇이 따라오고 무엇이
          즉시 바뀌는지를 그 자리에서 말한다. */}
      <ConfirmDialog
        open={serialConfirmOpen}
        title="로더 시리얼 변경"
        message={[
          `시리얼: ${loader.serial} → ${trimmedSerial}`,
          '',
          '• 장착 이력과 개조 기록은 그대로 따라옵니다.',
          '• 시리얼에서 읽어 내는 생산연월과 순번은 새 시리얼로 다시 계산됩니다.',
          '  규칙에 맞지 않는 시리얼이면 그 값은 비워지고, 자동 채번에서도 빠집니다.',
          '• 지금 장착 중인 로봇에 적힌 로더 번호도 함께 바뀝니다.',
          '• 같은 시리얼이 이미 있으면 바꿀 수 없습니다.',
          '',
          '계속하시겠습니까?',
        ].join('\n')}
        confirmLabel="변경 확인"
        busy={updateM.isPending}
        onConfirm={async () => {
          try {
            await updateM.mutateAsync();
          } catch (err) {
            // 중복 시리얼(409) 같은 서버 거부 사유를 확인창 안에서 그대로 보여준다.
            throw new Error(extractApiError(err));
          }
        }}
        onCancel={() => setSerialConfirmOpen(false)}
      />
    </div>
  );
}

const pickerList: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  maxHeight: 168,
  overflowY: 'auto',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  padding: 6,
};

const pickerRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 13,
  padding: '3px 4px',
  cursor: 'pointer',
  minWidth: 0,
};

const pickerName: CSSProperties = {
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  color: 'var(--fg-muted)',
};

const pickerNote: CSSProperties = {
  fontSize: 12,
  color: 'var(--fg-muted)',
  padding: '6px 2px',
};

const autoBadge: CSSProperties = {
  marginLeft: 'auto',
  flexShrink: 0,
  fontSize: 10,
  padding: '1px 6px',
  borderRadius: 'var(--r-pill)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-sunken)',
  color: 'var(--fg-muted)',
  whiteSpace: 'nowrap',
};

const selectStyle: CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  height: 34,
  padding: '0 10px',
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--bg-surface)',
  color: 'var(--fg-default)',
  fontSize: 13,
};
